import { useState } from 'react'
import { Button, Card, Form, Input, Tabs, Typography, message } from 'antd'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { authApi } from '@/api/auth'

const { Title, Text } = Typography

export default function LoginPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [verificationForm] = Form.useForm()
  const [verifiedEmail, setVerifiedEmail] = useState('')
  const [verificationToken, setVerificationToken] = useState('')
  const [sendingCode, setSendingCode] = useState(false)

  if (!auth.loading && auth.identity) return <Navigate to="/" replace />

  const destination =
    (location.state as { from?: string } | null)?.from || '/'
  const invitationToken = destination.startsWith('/accept-invitation')
    ? new URLSearchParams(destination.split('?')[1] || '').get('token')
    : null

  const login = async (values: { email: string; password: string }) => {
    try {
      await auth.login(values.email, values.password)
      navigate(destination, { replace: true })
    } catch (error) {
      message.error(error instanceof Error ? error.message : '登录失败')
    }
  }

  const register = async (values: {
    password: string
    display_name: string
    organization_name?: string
  }) => {
    try {
      await auth.register({
        ...values,
        email: verifiedEmail,
        verification_token: verificationToken,
        invitation_token: invitationToken || undefined,
      })
      navigate('/', { replace: true })
    } catch (error) {
      message.error(error instanceof Error ? error.message : '注册失败')
    }
  }

  const sendCode = async () => {
    try {
      const { email } = await verificationForm.validateFields(['email'])
      setSendingCode(true)
      const result = await authApi.sendRegistrationCode(email)
      if (result.debug_code) verificationForm.setFieldValue('code', result.debug_code)
      message.success(result.debug_code ? '开发模式验证码已自动填入' : '验证码已发送，请检查邮箱')
    } catch (error) {
      if (error instanceof Error) message.error(error.message)
    } finally {
      setSendingCode(false)
    }
  }

  const verifyEmail = async (values: { email: string; code: string }) => {
    try {
      const result = await authApi.verifyRegistrationCode(values.email, values.code)
      setVerifiedEmail(values.email.trim().toLowerCase())
      setVerificationToken(result.verification_token)
      message.success('邮箱验证成功，请设置登录密码')
    } catch (error) {
      message.error(error instanceof Error ? error.message : '验证码校验失败')
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'radial-gradient(circle at top, #172554, #020617 55%)',
        padding: 24,
      }}
    >
      <Card style={{ width: '100%', maxWidth: 430 }}>
        <Title level={2} style={{ marginBottom: 4 }}>米卡智能资管</Title>
        <Text type="secondary">企业账号与资产数据隔离</Text>
        <Tabs
          style={{ marginTop: 20 }}
          items={[
            {
              key: 'login',
              label: '登录',
              children: (
                <Form layout="vertical" onFinish={login}>
                  <Form.Item name="email" label="邮箱" rules={[{ required: true }, { type: 'email' }]}>
                    <Input autoComplete="email" />
                  </Form.Item>
                  <Form.Item name="password" label="密码" rules={[{ required: true }]}>
                    <Input.Password autoComplete="current-password" />
                  </Form.Item>
                  <Button htmlType="submit" type="primary" block>登录</Button>
                </Form>
              ),
            },
            {
              key: 'register',
              label: invitationToken ? '注册并加入企业' : '创建企业账号',
              children: (
                verificationToken ? (
                  <Form layout="vertical" onFinish={register}>
                    <Form.Item label="已验证邮箱">
                      <Input value={verifiedEmail} disabled />
                    </Form.Item>
                    {!invitationToken && (
                      <Form.Item name="organization_name" label="企业名称" rules={[{ required: true }]}>
                        <Input />
                      </Form.Item>
                    )}
                    <Form.Item name="display_name" label="姓名" rules={[{ required: true }]}>
                      <Input autoComplete="name" />
                    </Form.Item>
                    <Form.Item
                      name="password"
                      label="设置日常登录密码"
                      rules={[{ required: true }, { min: 8, message: '至少 8 个字符' }]}
                    >
                      <Input.Password autoComplete="new-password" />
                    </Form.Item>
                    <Button htmlType="submit" type="primary" block>
                      {invitationToken ? '注册并加入企业' : '完成注册并进入'}
                    </Button>
                  </Form>
                ) : (
                  <Form form={verificationForm} layout="vertical" onFinish={verifyEmail}>
                    <Form.Item name="email" label="邮箱" rules={[{ required: true }, { type: 'email' }]}>
                      <Input autoComplete="email" />
                    </Form.Item>
                    <Form.Item label="邮箱验证码" required>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Form.Item
                          name="code"
                          noStyle
                          rules={[
                            { required: true, message: '请输入验证码' },
                            { pattern: /^\d{6}$/, message: '请输入 6 位数字验证码' },
                          ]}
                        >
                          <Input inputMode="numeric" maxLength={6} />
                        </Form.Item>
                        <Button loading={sendingCode} onClick={() => void sendCode()}>
                          发送验证码
                        </Button>
                      </div>
                    </Form.Item>
                    <Button htmlType="submit" type="primary" block>验证邮箱</Button>
                  </Form>
                )
              ),
            },
          ]}
        />
      </Card>
    </div>
  )
}
